import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../../components/Layout';
import { toast } from 'sonner';
import { Loader2, UserPlus, KeyRound, Link2, Power, Trash2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SuperAdminUsers = () => {
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState('');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ email: '', name: '', role: 'User' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/superadmin/companies`).then((r) => {
      const list = (r.data || []).filter((c) => !c.deletedAt);
      setCompanies(list);
      if (list.length) setCompanyId(list[0].id);
    }).catch(() => toast.error('Failed to load companies'));
  }, []);

  const fetchUsers = async (cid) => {
    if (!cid) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/superadmin/companies/${cid}/users`);
      setUsers(r.data || []);
    } catch { toast.error('Failed to load users'); } finally { setLoading(false); }
  };

  useEffect(() => { fetchUsers(companyId); }, [companyId]);

  const createUser = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/superadmin/companies/${companyId}/users`, form);
      const tmp = r.data?.temporaryPassword;
      toast.success(tmp ? `User created. Temp password: ${tmp}` : 'User created; invite emailed.');
      setShowModal(false); setForm({ email: '', name: '', role: 'User' }); fetchUsers(companyId);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create user'); }
    finally { setSubmitting(false); }
  };

  const act = async (fn, ok) => { try { await fn(); toast.success(ok); fetchUsers(companyId); } catch (e) { toast.error(e.response?.data?.detail || 'Action failed'); } };
  const resetPw = (id) => act(async () => { const r = await axios.post(`${API}/superadmin/users/${id}/reset-password`); const t = r.data?.temporaryPassword; toast.message(t ? `Temp password: ${t}` : 'Reset email sent'); }, 'Password reset');
  const invite = (id) => act(async () => { const r = await axios.post(`${API}/superadmin/users/${id}/invite-link`); const l = r.data?.inviteLink || r.data?.token; if (l) navigator.clipboard?.writeText(l).catch(() => {}); toast.message(l ? `Invite link copied: ${String(l).slice(0, 40)}…` : 'Invite emailed'); }, 'Invite generated');
  const toggle = (u) => act(async () => axios.post(`${API}/superadmin/users/${u.id}/${u.status === 'active' ? 'deactivate' : 'activate'}`), 'Status updated');
  const del = (id) => { if (window.confirm('Delete this user permanently?')) act(async () => axios.delete(`${API}/superadmin/users/${id}`), 'User deleted'); };

  return (
    <Layout>
      <div className="space-y-6" data-testid="superadmin-users-page">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Users (All Companies)</h1>
            <p className="text-sm text-slate-600">Manage users for any tenant.</p>
          </div>
          <div className="flex items-center gap-3">
            <select value={companyId} onChange={(e) => setCompanyId(e.target.value)} className="h-10 px-3 border border-slate-300 rounded-lg text-sm" data-testid="sa-company-select">
              {companies.map((c) => <option key={c.id} value={c.id}>{c.companyName}</option>)}
            </select>
            <button onClick={() => setShowModal(true)} disabled={!companyId} className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm" data-testid="sa-add-user-btn">
              <UserPlus className="w-4 h-4" /> Add User
            </button>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          {loading ? (
            <div className="p-10 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" /></div>
          ) : users.length === 0 ? (
            <div className="p-10 text-center text-slate-500">No users in this company yet.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600 text-left">
                <tr><th className="p-3">Name</th><th className="p-3">Email</th><th className="p-3">Role</th><th className="p-3">Status</th><th className="p-3 text-right">Actions</th></tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-t border-slate-100" data-testid={`sa-user-row-${u.id}`}>
                    <td className="p-3 font-medium text-slate-900">{u.name}</td>
                    <td className="p-3 text-slate-600">{u.email}</td>
                    <td className="p-3">{u.role}</td>
                    <td className="p-3"><span className={`px-2 py-0.5 rounded-full text-xs ${u.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-slate-200 text-slate-600'}`}>{u.status || 'active'}</span></td>
                    <td className="p-3">
                      <div className="flex items-center justify-end gap-2">
                        <button title="Reset password" onClick={() => resetPw(u.id)} className="p-2 hover:bg-slate-100 rounded" data-testid={`sa-reset-${u.id}`}><KeyRound className="w-4 h-4 text-slate-600" /></button>
                        <button title="Invite link" onClick={() => invite(u.id)} className="p-2 hover:bg-slate-100 rounded" data-testid={`sa-invite-${u.id}`}><Link2 className="w-4 h-4 text-slate-600" /></button>
                        <button title="Activate/Deactivate" onClick={() => toggle(u)} className="p-2 hover:bg-slate-100 rounded" data-testid={`sa-toggle-${u.id}`}><Power className={`w-4 h-4 ${u.status === 'active' ? 'text-amber-600' : 'text-green-600'}`} /></button>
                        <button title="Delete" onClick={() => del(u.id)} className="p-2 hover:bg-red-50 rounded" data-testid={`sa-delete-${u.id}`}><Trash2 className="w-4 h-4 text-red-600" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setShowModal(false)}>
          <div className="bg-white rounded-xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="sa-create-user-modal">
            <h2 className="text-lg font-semibold mb-4">Add User</h2>
            <form onSubmit={createUser} className="space-y-4">
              <input required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full h-11 px-3 border border-slate-300 rounded-lg" data-testid="sa-user-email" />
              <input required placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full h-11 px-3 border border-slate-300 rounded-lg" data-testid="sa-user-name" />
              <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full h-11 px-3 border border-slate-300 rounded-lg" data-testid="sa-user-role">
                <option value="User">User</option>
                <option value="CompanyAdmin">Company Admin</option>
              </select>
              <p className="text-xs text-slate-500">A secure temporary password is generated; the user sets their own on first login.</p>
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 text-slate-600">Cancel</button>
                <button type="submit" disabled={submitting} className="px-5 py-2 bg-blue-600 text-white rounded-lg disabled:opacity-50 flex items-center gap-2" data-testid="sa-create-user-submit">
                  {submitting && <Loader2 className="w-4 h-4 animate-spin" />} Create
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default SuperAdminUsers;
